import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Box, Typography, Paper, Table, TableBody, TableCell, TableHead, 
    TableRow, TableContainer, Chip, IconButton, Tooltip, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Accordion,
    AccordionSummary, AccordionDetails, Grow, Card, CardContent, Divider
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';

import { 
    Grid, TextField, FormControl, InputLabel, Select, MenuItem, InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import API_URL from '../config';

const Logs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLog, setSelectedLog] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);

    const [filterUser, setFilterUser] = useState('');
    const [filterModule, setFilterModule] = useState('All');
    const [filterAction, setFilterAction] = useState('All');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchLogs();
    }, []);

    const fetchLogs = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/logs`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.data && res.data.success) {
                setLogs(res.data.logs);
            }
        } catch (error) {
            console.error('Error fetching logs', error);
        } finally {
            setLoading(false);
        }
    };

    const handleViewLog = (log) => {
        setSelectedLog(log);
        setOpenDialog(true);
    };

    // Render detailed JSON old/new visually
    const renderDetails = (detailsStr) => {
        try {
            const dt = JSON.parse(detailsStr);
            if (!dt.changes || dt.changes.length === 0) return <Typography color="textSecondary">No changes detected.</Typography>;

            return (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b' }}>Field Modified</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b', width: '40%' }}>Previous Value</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#64748b', width: '40%' }}>New Value</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dt.changes.map((c, i) => (
                                <TableRow key={i} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#334155' }}>
                                        {c.field}
                                    </TableCell>
                                    <TableCell sx={{ bgcolor: '#fef2f2', color: '#ef4444', wordBreak: 'break-word', borderRight: '1px dashed #e2e8f0' }}>
                                        {String(c.old_value) === 'null' || String(c.old_value) === 'undefined' ? <Typography variant="caption" color="textSecondary">None</Typography> : <del>{String(c.old_value)}</del>}
                                    </TableCell>
                                    <TableCell sx={{ bgcolor: '#f0fdf4', color: '#10b981', wordBreak: 'break-word' }}>
                                        {String(c.new_value) === 'null' || String(c.new_value) === 'undefined' ? <Typography variant="caption" color="textSecondary">None</Typography> : String(c.new_value)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        } catch (e) {
            return (
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', color: '#334155' }}>
                        {detailsStr}
                    </Typography>
                </Paper>
            );
        }
    };

    const filteredLogs = logs.filter(log => {
        const matchUser = filterUser ? (log.user || '').toLowerCase().includes(filterUser.toLowerCase()) : true;
        const matchModule = filterModule !== 'All' ? log.module === filterModule : true;
        const matchAction = filterAction !== 'All' ? log.action === filterAction : true;
        const matchSearch = searchTerm ? 
            (log.module || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (log.action || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (log.details || '').toLowerCase().includes(searchTerm.toLowerCase()) : true;
            
        let matchDate = true;
        if (startDate && endDate) {
            const logDate = new Date(log.timestamp).getTime();
            const sDate = new Date(startDate).getTime();
            const eDate = new Date(endDate).getTime() + 86400000; // include end of day
            matchDate = logDate >= sDate && logDate <= eDate;
        }
        
        return matchUser && matchModule && matchAction && matchSearch && matchDate;
    });

    const uniqueModules = [...new Set(logs.map(l => l.module).filter(Boolean))];
    const uniqueActions = [...new Set(logs.map(l => l.action).filter(Boolean))];

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden', boxSizing: 'border-box', pb: 6, px: { xs: 1, md: 3 }, bgcolor: '#f4f6f8', minHeight: '100vh' }}>
            
            {/* Modern Gradient Header exactly like Tickets */}
            <Grow in={true} timeout={500}>
                <Box sx={{ 
                    mb: 4, 
                    mt: 2, 
                    p: 4, 
                    borderRadius: 4, 
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
                    color: 'white',
                    boxShadow: '0 10px 30px rgba(30, 60, 114, 0.2)',
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 3
                }}>
                    <Box sx={{ position: 'relative', zIndex: 2 }}>
                        <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}>
                            <HistoryIcon sx={{ fontSize: 36, color: '#90caf9' }} />
                            System Activity Logs
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            Monitor and audit all database actions across the platform.
                        </Typography>
                    </Box>
                    <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                    <Box sx={{ position: 'absolute', bottom: -100, right: 100, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                </Box>
            </Grow>

            {/* Filters and Controls */}
            <Card elevation={0} sx={{ mb: 4, p: 2, borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', lg: 'center' }, gap: 2 }}>
                    
                    {/* Left side tags / inputs */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', flexGrow: 1 }}>
                        <TextField
                            placeholder="Search details..."
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>
                            }}
                            sx={{
                                minWidth: 200,
                                bgcolor: '#f8fafc',
                                borderRadius: 2,
                                '& fieldset': { borderColor: '#e2e8f0', borderRadius: 2 },
                                '&:hover fieldset': { borderColor: '#cbd5e1' }
                            }}
                        />

                        <TextField
                            placeholder="Filter by User"
                            size="small"
                            value={filterUser}
                            onChange={(e) => setFilterUser(e.target.value)}
                            sx={{
                                minWidth: 150,
                                bgcolor: '#f8fafc',
                                borderRadius: 2,
                                '& fieldset': { borderColor: '#e2e8f0', borderRadius: 2 }
                            }}
                        />

                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                                value={filterModule}
                                onChange={(e) => setFilterModule(e.target.value)}
                                displayEmpty
                                sx={{ borderRadius: 2, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } }}
                            >
                                <MenuItem value="All"><Typography color="textSecondary" sx={{ fontSize: '0.875rem' }}>All Modules</Typography></MenuItem>
                                {uniqueModules.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <Select
                                value={filterAction}
                                onChange={(e) => setFilterAction(e.target.value)}
                                displayEmpty
                                sx={{ borderRadius: 2, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } }}
                            >
                                <MenuItem value="All"><Typography color="textSecondary" sx={{ fontSize: '0.875rem' }}>All Actions</Typography></MenuItem>
                                {uniqueActions.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Right side dates */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            size="small"
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            sx={{ minWidth: 140, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0', borderRadius: 2 } }}
                        />
                        <Typography color="textSecondary" sx={{ fontWeight: 'bold' }}>To</Typography>
                        <TextField
                            size="small"
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            sx={{ minWidth: 140, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0', borderRadius: 2 } }}
                        />
                    </Box>
                </Box>
            </Card>

            {loading ? (
                <Box display="flex" justifyContent="center" my={5}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead sx={{ bgcolor: 'grey.50' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Date & Time</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Module</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Action</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Entity ID</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Details</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredLogs.map((log) => (
                                <TableRow key={log.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{log.user}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={log.module || '-'} size="small" variant="outlined" sx={{ fontWeight: 'bold' }} />
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={log.action} 
                                            size="small"
                                            color={
                                                log.action.includes('Edit') ? 'warning' :
                                                log.action.includes('Create') || log.action.includes('Add') ? 'success' :
                                                log.action === 'Login' ? 'info' : 'default'
                                            }
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{log.entity_id || '-'}</Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="View Changes">
                                            <IconButton color="primary" onClick={() => handleViewLog(log)}>
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredLogs.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                        <Typography variant="body1" color="text.secondary">
                                            No activity logs found matching your filters.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Detail Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                {selectedLog && (
                    <>
                        <DialogTitle sx={{ bgcolor: '#1e3c72', color: 'white', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <HistoryIcon sx={{ color: '#90caf9' }} /> 
                                Audit Entry Details - Record #{selectedLog.id}
                            </Box>
                            <IconButton size="small" onClick={() => setOpenDialog(false)} sx={{ color: 'white' }}>
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#f8fafc' }}>
                            {/* Meta Info Banner */}
                            <Paper elevation={0} sx={{ mb: 4, display: 'flex', gap: 2, flexWrap: 'wrap', p: 3, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: 'white' }}>
                                <Box sx={{ flex: 1, minWidth: 150 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 800 }}>Triggered By</Typography>
                                    <Typography variant="body1" fontWeight="bold" sx={{ color: '#334155', display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <PersonIcon sx={{ color: '#94a3b8', fontSize: 20 }} /> {selectedLog.user}
                                    </Typography>
                                </Box>
                                <Divider orientation="vertical" flexItem sx={{ borderColor: '#f1f5f9' }} />
                                <Box sx={{ flex: 1, minWidth: 150 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 800 }}>System Area</Typography>
                                    <Typography variant="body1" fontWeight="bold" sx={{ color: '#334155', mt: 1 }}>
                                        {selectedLog.module} <span style={{ color: '#cbd5e1', margin: '0 4px' }}>|</span> #{selectedLog.entity_id || 'N/A'}
                                    </Typography>
                                </Box>
                                <Divider orientation="vertical" flexItem sx={{ borderColor: '#f1f5f9' }} />
                                <Box sx={{ flex: 1, minWidth: 150 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 800 }}>Event</Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <Chip 
                                            label={selectedLog.action} 
                                            size="medium"
                                            sx={{ 
                                                fontWeight: 'bold',
                                                bgcolor: selectedLog.action.includes('Edit') ? '#fff7ed' : 
                                                         selectedLog.action.includes('Create') || selectedLog.action.includes('Add') ? '#f0fdf4' : '#f8fafc',
                                                color: selectedLog.action.includes('Edit') ? '#ea580c' : 
                                                       selectedLog.action.includes('Create') || selectedLog.action.includes('Add') ? '#16a34a' : '#475569',
                                                borderColor: selectedLog.action.includes('Edit') ? '#fed7aa' : 
                                                             selectedLog.action.includes('Create') || selectedLog.action.includes('Add') ? '#bbf7d0' : '#e2e8f0',
                                                border: '1px solid'
                                            }} 
                                        />
                                    </Box>
                                </Box>
                                <Divider orientation="vertical" flexItem sx={{ borderColor: '#f1f5f9' }} />
                                <Box sx={{ flex: 1, minWidth: 150 }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 800 }}>Time</Typography>
                                    <Typography variant="body2" sx={{ color: '#334155', mt: 1, fontWeight: 'medium' }}>
                                        {new Date(selectedLog.timestamp).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                                    </Typography>
                                </Box>
                            </Paper>

                            <Typography variant="subtitle2" sx={{ mb: 3, fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                                Detailed Data Changes
                            </Typography>
                            {renderDetails(selectedLog.details)}
                        </DialogContent>
                        <DialogActions sx={{ p: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                            <Button onClick={() => setOpenDialog(false)} variant="outlined" sx={{ fontWeight: 'bold' }}>
                                Close
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default Logs;
